/**
 * Map API client functions for WP 5.1 endpoints
 */
import { apiClient } from "../client";
import { isAllCampaignsMode, resolveMapCampaignIdForApi } from "./campaignScope";
import type {
    RoadsResponse,
    AddressesResponse,
    AddressesStaticResponse,
    AddressesDeliveriesResponse,
    AssignmentsResponse,
    AddressGridAggregationsResponse,
    PostalCodesResponse,
    ElectoralDistrictsResponse,
    MunicipalitiesResponse,
    CountyOption,
    RoadsQueryParams,
    AddressesQueryParams,
    AssignmentsQueryParams,
    MapAddressAggregationsQueryParams,
    PostalCodesQueryParams,
    ElectoralDistrictsQueryParams,
    MunicipalitiesQueryParams
} from "./types";

const MAP_API_BASE = "/map";

/**
 * Fetches roads GeoJSON data
 */
export const fetchRoads = async (
    params: RoadsQueryParams,
    signal?: AbortSignal
): Promise<RoadsResponse> => {
    const response = await apiClient.get<RoadsResponse>(
        `${MAP_API_BASE}/roads`,
        {
            params: {
                campaignId: params.campaignId,
                flyerTypeId: params.flyerTypeId,
                bbox: params.bbox,
                updatedSince: params.updatedSince,
                limit: params.limit,
                includeMetadata: params.includeMetadata ?? true
            },
            signal // Pass cancellation signal to axios
        }
    );
    return response.data;
};

/**
 * Fetches addresses GeoJSON data
 */
export const fetchAddresses = async (
    params: AddressesQueryParams,
    signal?: AbortSignal
): Promise<AddressesResponse> => {
    const response = await apiClient.get<AddressesResponse>(
        `${MAP_API_BASE}/addresses`,
        {
            params: {
                campaignId: params.campaignId,
                flyerTypeId: params.flyerTypeId,
                bbox: params.bbox,
                updatedSince: params.updatedSince,
                limit: params.limit,
                includeMetadata: params.includeMetadata ?? true,
                includeHidden: params.includeHidden ?? true, // Default to true to show all addresses
                includeBlocked: params.includeBlocked ?? true, // Default to true to show all addresses
                assignmentId: params.assignmentId
            },
            signal // Pass cancellation signal to axios
        }
    );
    return response.data;
};

/**
 * Fetches static address locations GeoJSON data (Phase 1: Split endpoints)
 * Static data includes location, street, city, etc. (never changes except on import)
 */
export const fetchAddressesStatic = async (
    params: AddressesQueryParams,
    signal?: AbortSignal
): Promise<AddressesStaticResponse> => {
    const apiCampaignId = resolveMapCampaignIdForApi(params.campaignId);
    const allCampaigns = isAllCampaignsMode(params.campaignId);
    const debugMsg = `[fetchAddressesStatic] Request campaignId=${apiCampaignId ?? "all"}, bbox=${params.bbox?.substring(0, 30)}`;
    console.log(debugMsg);
    if (typeof (window as any).cfDebugLog === 'function') {
        (window as any).cfDebugLog(debugMsg);
    }
    const response = await apiClient.get<AddressesStaticResponse>(
        `${MAP_API_BASE}/addresses/static`,
        {
            params: {
                campaignId: apiCampaignId,
                bbox: params.bbox,
                limit: allCampaigns ? undefined : params.limit,
                includeMetadata: params.includeMetadata ?? true,
                includeHidden: allCampaigns ? false : (params.includeHidden ?? true),
                includeBlocked: allCampaigns ? false : (params.includeBlocked ?? true),
                includeUndelivered: allCampaigns ? (params.includeUndelivered ?? false) : undefined,
                assignmentId: params.assignmentId
            },
            signal
        }
    );
    const responseMsg = `[fetchAddressesStatic] Response features=${response.data?.features?.length ?? 0}`;
    console.log(responseMsg);
    if (typeof (window as any).cfDebugLog === 'function') {
        (window as any).cfDebugLog(responseMsg);
    }
    return response.data;
};

/**
 * Fetches address delivery updates GeoJSON data (Phase 1: Split endpoints)
 * Dynamic data includes only delivery statuses (changes frequently)
 */
export const fetchAddressDeliveries = async (
    params: AddressesQueryParams,
    updatedSince?: string,
    signal?: AbortSignal
): Promise<AddressesDeliveriesResponse> => {
    const apiCampaignId = resolveMapCampaignIdForApi(params.campaignId);
    const allCampaigns = isAllCampaignsMode(params.campaignId);
    const requestParams = {
        campaignId: apiCampaignId,
        flyerTypeId: apiCampaignId ? params.flyerTypeId : undefined,
        bbox: params.bbox,
        limit: allCampaigns ? undefined : params.limit,
        includeMetadata: params.includeMetadata ?? true,
        includeHidden: allCampaigns ? false : (params.includeHidden ?? true),
        includeBlocked: allCampaigns ? false : (params.includeBlocked ?? true),
        includeUndelivered: allCampaigns ? (params.includeUndelivered ?? false) : undefined,
        assignmentId: params.assignmentId,
        updatedSince: updatedSince
    };

    // Debug: Log cache-related request details to diagnose green circle caching issues
    const isDeltaRequest = !!updatedSince;
    if (isDeltaRequest || params.flyerTypeId) {
        console.log(`[fetchAddressDeliveries] ${isDeltaRequest ? "Delta" : "Full"} request:`, {
            campaignId: requestParams.campaignId,
            flyerTypeId: requestParams.flyerTypeId,
            bbox: requestParams.bbox,
            updatedSince: requestParams.updatedSince,
            requestType: isDeltaRequest ? "delta (updatedSince set)" : "full (no updatedSince)"
        });
    }

    const response = await apiClient.get<AddressesDeliveriesResponse>(
        `${MAP_API_BASE}/addresses/deliveries`,
        {
            params: requestParams,
            signal
        }
    );

    // Debug: Log response details to diagnose cache issues
    const featureCount = response.data.features.length;
    const deliveryCount = response.data.features.reduce((sum, f) => sum + (f.properties.deliveries?.length || 0), 0);

    if (isDeltaRequest || params.flyerTypeId || featureCount === 0) {
        console.log(`[fetchAddressDeliveries] Response:`, {
            requestType: isDeltaRequest ? "delta" : "full",
            featureCount,
            deliveryCount,
            deliveryUpdateCount: response.data.metadata.deliveryUpdateCount,
            lastUpdated: response.data.metadata.lastUpdated,
            isEmpty: featureCount === 0,
            warning: featureCount === 0 && !isDeltaRequest ? "Empty full response - may indicate cache issue" : undefined
        });
    }

    return response.data;
};

/**
 * Fetches assignments GeoJSON data
 */
export const fetchAssignments = async (
    params: AssignmentsQueryParams
): Promise<AssignmentsResponse> => {
    const response = await apiClient.get<AssignmentsResponse>(
        `${MAP_API_BASE}/assignments`,
        {
            params: {
                campaignId: params.campaignId,
                municipalityId: params.municipalityId,
                flyerTypeId: params.flyerTypeId,
                bbox: params.bbox,
                updatedSince: params.updatedSince,
                limit: params.limit,
                includeMetadata: params.includeMetadata ?? true,
                distributorId: params.distributorId,
                status: params.status
            }
        }
    );
    return response.data;
};

/**
 * Fetches address grid aggregations GeoJSON data (Phase 2: Database Aggregations)
 * Returns aggregated address data based on geohash precision for different zoom levels
 */
export const fetchAddressAggregations = async (
    params: MapAddressAggregationsQueryParams,
    signal?: AbortSignal
): Promise<AddressGridAggregationsResponse> => {
    const response = await apiClient.get<AddressGridAggregationsResponse>(
        `${MAP_API_BASE}/addresses/aggregations`,
        {
            params: {
                campaignId: params.campaignId,
                bbox: params.bbox,
                precisions: params.precisions,
                zoomLevel: params.zoomLevel
            },
            signal
        }
    );
    return response.data;
};

/**
 * Fetches postal code polygons GeoJSON data
 */
export const fetchPostalCodes = async (
    params: PostalCodesQueryParams,
    signal?: AbortSignal
): Promise<PostalCodesResponse> => {
    const response = await apiClient.get<PostalCodesResponse>(
        `${MAP_API_BASE}/postal-codes`,
        {
            params: {
                postalCodes: params.postalCodes,
                municipalityCode: params.municipalityCode,
                bbox: params.bbox,
                limit: params.limit,
                includeMetadata: params.includeMetadata ?? true
            },
            signal
        }
    );
    return response.data;
};

/**
 * Fetches electoral district polygons GeoJSON data
 */
export const fetchElectoralDistricts = async (
    params: ElectoralDistrictsQueryParams,
    signal?: AbortSignal
): Promise<ElectoralDistrictsResponse> => {
    const response = await apiClient.get<ElectoralDistrictsResponse>(
        `${MAP_API_BASE}/electoral-districts`,
        {
            params: {
                codes: params.codes,
                municipalityCode: params.municipalityCode,
                bbox: params.bbox,
                limit: params.limit,
                includeMetadata: params.includeMetadata ?? true
            },
            signal
        }
    );
    return response.data;
};

/**
 * Fetches municipality (kommun) polygons GeoJSON data
 */
export const fetchMunicipalities = async (
    params: MunicipalitiesQueryParams,
    signal?: AbortSignal
): Promise<MunicipalitiesResponse> => {
    const response = await apiClient.get<MunicipalitiesResponse>(
        `${MAP_API_BASE}/municipalities`,
        {
            params: {
                codes: params.codes,
                countyId: params.countyId,
                bbox: params.bbox,
                limit: params.limit,
                includeMetadata: params.includeMetadata ?? true,
                includeGeometry: params.includeGeometry
            },
            signal
        }
    );
    return response.data;
};

/**
 * Fetches counties (län) for the map layers dropdown
 */
export const fetchCounties = async (signal?: AbortSignal): Promise<CountyOption[]> => {
    const response = await apiClient.get<CountyOption[]>(`${MAP_API_BASE}/counties`, { signal });
    return response.data;
};